import { useState } from "react";
import { NUM, unitLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Warehouse, Store, ArrowRight, ArrowLeft, GripVertical, MoveHorizontal } from "lucide-react";
import { Thumb } from "./PortalTable";

const META = {
  depo: { title: "Depo", Icon: Warehouse, col: "bg-slate-50 border-slate-300", over: "ring-2 ring-slate-500 bg-slate-100", qty: "text-slate-700", other: "magaza", otherTr: "Mağaza'ya" },
  magaza: { title: "Satış Mağazası", Icon: Store, col: "bg-teal-50/40 border-teal-200", over: "ring-2 ring-teal-500 bg-teal-100/50", qty: "text-teal-700", other: "depo", otherTr: "Depo'ya" },
};

function Card({ p, loc, sel, onDragStart, onDragEnd, onMoveOne }) {
  const m = META[loc];
  const q = loc === "depo" ? p.qty_depo : p.qty_magaza;
  return (
    <div draggable onDragStart={(e) => onDragStart(e, p, loc)} onDragEnd={onDragEnd}
      className={cn("bg-white p-2.5 rounded-lg border shadow-xs hover:shadow-md cursor-grab active:cursor-grabbing transition-all flex items-center gap-2.5", sel ? "border-amber-400 ring-1 ring-amber-300" : "border-slate-200")}
      data-testid={`board-card-${loc}-${p.id}`}>
      <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
      <Thumb p={p} size="h-9 w-9" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-slate-800 leading-snug break-words">{p.name}</div>
        <div className="text-[11px] text-slate-500 truncate">{[p.brand, p.code].filter(Boolean).join(" · ")}</div>
      </div>
      <div className="text-right shrink-0">
        <div className={cn("tnum font-black text-[15px] leading-4", m.qty)}>{NUM(q)}</div>
        <div className="text-[10px] text-slate-400">{unitLabel(p.unit)}</div>
      </div>
      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" title={`Tümünü ${m.otherTr} taşı`} onClick={() => onMoveOne(p, m.other, q)} data-testid={`board-move-${loc}-${p.id}`}>
        {loc === "depo" ? <ArrowRight className="h-4 w-4 text-teal-600" /> : <ArrowLeft className="h-4 w-4 text-slate-600" />}
      </Button>
    </div>
  );
}

function Column({ loc, items, drag, setDrag, selected, onDropIds, onMoveOne, onMoveColumn }) {
  const m = META[loc];
  const [over, setOver] = useState(false);
  const list = items.filter((p) => (loc === "depo" ? p.qty_depo : p.qty_magaza) > 0);
  const total = list.reduce((s, p) => s + (loc === "depo" ? p.qty_depo : p.qty_magaza), 0);
  const canDrop = drag && drag.from !== loc;
  const onDragStart = (e, p, from) => {
    const ids = selected.includes(p.id) && selected.length > 1 ? selected : [p.id];
    e.dataTransfer.setData("text/plain", JSON.stringify({ ids, from }));
    e.dataTransfer.effectAllowed = "move";
    setDrag({ from, ids, p });
  };
  return (
    <div onDragOver={(e) => { if (canDrop) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (canDrop) onDropIds(drag, loc); setDrag(null); }}
      className={cn("p-3 sm:p-4 rounded-xl border-2 border-dashed min-h-[420px] transition-all", m.col, over && m.over)} data-testid={`board-column-${loc}`}>
      <div className="flex items-center justify-between gap-2 pb-3 mb-3 border-b border-slate-200 flex-wrap">
        <div className="flex items-center gap-2">
          <m.Icon className={cn("h-5 w-5", m.qty)} />
          <div>
            <div className="font-head font-bold text-[#0F2745]">{m.title}</div>
            <div className="text-[11px] text-slate-500"><span className="tnum font-semibold">{list.length}</span> ürün · <span className="tnum font-semibold">{NUM(Math.round(total * 100) / 100)}</span> birim</div>
          </div>
        </div>
        {list.length > 0 && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onMoveColumn(list, m.other)} data-testid={`board-move-all-${loc}`}><MoveHorizontal className="h-3.5 w-3.5 mr-1" />Hepsini {m.otherTr}</Button>}
      </div>
      {canDrop && <div className={cn("mb-3 rounded-lg border-2 border-dashed py-3 text-center text-xs font-semibold", loc === "magaza" ? "border-teal-400 text-teal-700" : "border-slate-400 text-slate-600")}>Buraya bırakın · {drag.ids.length > 1 ? `${drag.ids.length} ürün` : "tüm stok"} {m.title}'ya taşınır</div>}
      <div className="space-y-2">
        {list.map((p) => <Card key={p.id} p={p} loc={loc} sel={selected.includes(p.id)} onDragStart={onDragStart} onDragEnd={() => setDrag(null)} onMoveOne={onMoveOne} />)}
        {!list.length && <div className="text-center text-sm text-slate-400 py-12">Bu sayfadaki ürünlerde {m.title.toLocaleLowerCase("tr")} stoğu yok</div>}
      </div>
    </div>
  );
}

/** Depo ↔ Mağaza panosu: kartı diğer sütuna sürükleyince o bölümdeki stoğun tamamı ANINDA taşınır (seçiliyse tüm seçim). */
export function PortalBoard({ items, selected, onMoveOne, onMoveIds }) {
  const [drag, setDrag] = useState(null);
  const onDropIds = (d, to) => {
    if (d.ids.length === 1) { const q = d.from === "depo" ? d.p.qty_depo : d.p.qty_magaza; onMoveOne(d.p, to, q); }
    else onMoveIds(d.ids, to);
  };
  const onMoveColumn = (list, to) => onMoveIds(list.map((p) => p.id), to);
  const props = { items, drag, setDrag, selected, onDropIds, onMoveOne, onMoveColumn };
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">Kartı tutup diğer sütuna bırakın veya ok düğmesine basın. Seçili ürünlerden birini sürüklerseniz <b>tüm seçim</b> taşınır. Kısmi adet için tablodaki kaydırıcıyı kullanın.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="portal-board">
        <Column loc="depo" {...props} />
        <Column loc="magaza" {...props} />
      </div>
    </div>
  );
}
